import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { TagChip, TextInput } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import type { ChecklistNodeLink } from '../../types/schema'
import {
  CURRENCIES,
  getCurrencySymbol,
  RATING_DIMENSION_ORDER,
  RATING_DIMENSIONS,
  type RatingDimensionKey,
  WORKFLOW_STATUS_OPTIONS,
  filterWorkflowStatusOptions,
} from '../../utils/checklistNodeMeta'
import type { ChecklistWorkflowStatus } from '../../types/schema'

// ===========================================================================
// CollapsibleSection — tier card with a rotating MUI chevron at
// the start of the heading and an always-visible status chip on the right.
// ===========================================================================

interface CollapsibleSectionProps {
  title: string
  chip?: ReactNode
  defaultOpen?: boolean
  className?: string
  children: ReactNode
}

export const CollapsibleSection = ({
  title,
  chip,
  defaultOpen = false,
  className = '',
  children,
}: CollapsibleSectionProps) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section className={`nodeTierCard nodeCollapsibleCard ${open ? 'isOpen' : ''} ${className}`}>
      <div className="nodeCollapsibleHeader">
        <Pressable
          className="nodeCollapseToggle"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className={`nodeCollapseChevron ${open ? 'isOpen' : ''}`}>
            <ChevronRightIcon fontSize="inherit" />
          </span>
          <span className="nodeCollapseTitle">{title}</span>
        </Pressable>
        {chip}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="nodeCollapsibleBody"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="nodeCollapsibleBodyInner">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

// ===========================================================================
// RatingMatrix — three 1–5 star columns (Complexity / Urgency / Importance)
// with hover labels and a click toast emitted via onSelect.
// ===========================================================================

interface RatingMatrixProps {
  values: Record<RatingDimensionKey, number>
  disabled?: boolean
  onSelect: (key: RatingDimensionKey, star: number) => void
}

export const RatingMatrix = ({ values, disabled = false, onSelect }: RatingMatrixProps) => {
  const [hover, setHover] = useState<{ key: RatingDimensionKey; star: number } | null>(null)
  return (
    <div className="nodeRatingMatrix">
      {RATING_DIMENSION_ORDER.map((key) => {
        const dimension = RATING_DIMENSIONS[key]
        const value = values[key]
        const activeHover = hover?.key === key ? hover.star : null
        const shown = activeHover ?? value
        return (
          <div className="nodeRatingColumn" key={key}>
            <span className="nodeRatingLabel">{dimension.label}</span>
            <div className="nodeRatingStars" role="radiogroup" aria-label={dimension.label}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Pressable
                  key={star}
                  role="radio"
                  aria-checked={star === value}
                  aria-label={`${dimension.label} ${star} — ${dimension.optionLabels[star - 1]}`}
                  title={`${star} · ${dimension.optionLabels[star - 1]}`}
                  className={`nodeRatingStar ${star <= shown ? 'isActive' : ''}`}
                  disabled={disabled}
                  onMouseEnter={() => setHover({ key, star })}
                  onMouseLeave={() => setHover((current) => (current?.key === key ? null : current))}
                  onFocus={() => setHover({ key, star })}
                  onBlur={() => setHover((current) => (current?.key === key ? null : current))}
                  onClick={() => onSelect(key, star)}
                >
                  {star <= shown ? (
                    <StarRoundedIcon fontSize="inherit" />
                  ) : (
                    <StarOutlineRoundedIcon fontSize="inherit" />
                  )}
                </Pressable>
              ))}
            </div>
            <span className="nodeRatingValueLabel">{dimension.optionLabels[shown - 1]}</span>
          </div>
        )
      })}
    </div>
  )
}

// ===========================================================================
// ScrollDial — vertical increment/decrement dial. Scroll-up or drag-up
// increases, scroll-down/drag-down decreases. Integer steps, clamped to min.
// ===========================================================================

interface ScrollDialProps {
  value: number
  min: number
  max?: number
  /** Scroll/drag step (per wheel tick / per `DIAL_DRAG_PX_PER_STEP` px of drag).
   * Typed input is always a free integer; this only governs scroll/drag. */
  step?: number
  disabled?: boolean
  ariaLabel: string
  caption?: string
  /** Rendered before the value (e.g. the currency button). */
  leading?: ReactNode
  onChange: (next: number) => void
}

const DIAL_DRAG_PX_PER_STEP = 14

export const ScrollDial = ({
  value,
  min,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  disabled = false,
  ariaLabel,
  caption,
  leading,
  onChange,
}: ScrollDialProps) => {
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const dragRef = useRef<{ startY: number; startValue: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const [focused, setFocused] = useState(false)

  // Local draft → persists (onChange) on release / after a short settle (scroll)
  // / on blur (typed input). Prevents wheel-spin or drag from flooding writes.
  const [draft, setDraft] = useState(value)
  // While focused, `inputText` holds raw keystrokes (commas stripped). On blur
  // it's parsed/clamped and the draft + commit fire.
  const [inputText, setInputText] = useState(String(value))
  const draftRef = useRef(value)
  const commitTimerRef = useRef<number | null>(null)

  const clamp = (next: number) => Math.max(min, Math.min(max, Math.round(next)))

  // Adopt external changes (committed writes, other collaborators) when not
  // mid-drag and not currently typing.
  useEffect(() => {
    if (dragRef.current || focused) return
    setDraft(value)
    draftRef.current = value
    setInputText(String(value))
  }, [value, focused])

  const commitNow = () => {
    if (commitTimerRef.current !== null) {
      window.clearTimeout(commitTimerRef.current)
      commitTimerRef.current = null
    }
    if (draftRef.current !== value) onChange(draftRef.current)
  }

  const scheduleCommit = () => {
    if (commitTimerRef.current !== null) window.clearTimeout(commitTimerRef.current)
    commitTimerRef.current = window.setTimeout(() => {
      commitTimerRef.current = null
      if (draftRef.current !== value) onChange(draftRef.current)
    }, 320)
  }

  const setLocal = (next: number, commit: 'now' | 'settle') => {
    if (disabled) return
    const clamped = clamp(next)
    draftRef.current = clamped
    setDraft(clamped)
    setInputText(String(clamped))
    if (commit === 'now') commitNow()
    else scheduleCommit()
  }

  const bump = (direction: number) => setLocal(draftRef.current + direction * step, 'now')

  useEffect(() => () => {
    if (commitTimerRef.current !== null) window.clearTimeout(commitTimerRef.current)
  }, [])

  // Wheel must be non-passive so we can preventDefault the scroll. Wheel steps
  // also apply when the input is focused (typing still works via keyboard).
  useEffect(() => {
    const surface = surfaceRef.current
    if (!surface) return
    const handleWheel = (event: WheelEvent) => {
      if (disabled) return
      event.preventDefault()
      setLocal(draftRef.current + (event.deltaY < 0 ? step : -step), 'settle')
    }
    surface.addEventListener('wheel', handleWheel, { passive: false })
    return () => surface.removeEventListener('wheel', handleWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, min, max, value, step])

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    // Don't start a drag if the user clicks INTO the input to type.
    if (event.target instanceof HTMLInputElement) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = { startY: event.clientY, startValue: draftRef.current }
    setDragging(true)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || disabled) return
    const deltaSteps = Math.round((dragRef.current.startY - event.clientY) / DIAL_DRAG_PX_PER_STEP)
    const next = clamp(dragRef.current.startValue + deltaSteps * step)
    draftRef.current = next
    setDraft(next)
    setInputText(String(next))
  }

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current && event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const wasDragging = dragRef.current !== null
    dragRef.current = null
    setDragging(false)
    if (wasDragging) commitNow()
  }

  const handleInputCommit = () => {
    // Strip commas/non-digits, clamp, fire onChange.
    const raw = inputText.replace(/[^0-9-]/g, '')
    const parsed = raw && raw !== '-' ? Number.parseInt(raw, 10) : draft
    const clamped = clamp(Number.isFinite(parsed) ? parsed : draft)
    draftRef.current = clamped
    setDraft(clamped)
    setInputText(String(clamped))
    setFocused(false)
    if (clamped !== value) onChange(clamped)
  }

  // Comma-formatted display when not focused; raw digits while typing.
  const display = focused ? inputText : draft.toLocaleString('en-US')

  return (
    <div className={`nodeScrollDial ${disabled ? 'isDisabled' : ''} ${dragging ? 'isDragging' : ''}`}>
      <IconButton
        variant="tertiary"
        size="sm"
        className="nodeScrollDialStep"
        label={`Increase ${ariaLabel}`}
        disabled={disabled || draft >= max}
        icon={<KeyboardArrowUpIcon fontSize="inherit" />}
        onClick={() => bump(1)}
      />
      <div
        ref={surfaceRef}
        className="nodeScrollDialSurface"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {leading}
        <TextInput
          ref={inputRef}
          className="nodeScrollDialInput"
          inputMode="numeric"
          aria-label={ariaLabel}
          disabled={disabled}
          value={display}
          onFocus={() => {
            setFocused(true)
            setInputText(String(draft))
          }}
          onChange={(next) => setInputText(next.replace(/[^0-9-]/g, ''))}
          onBlur={handleInputCommit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur()
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              bump(1)
            } else if (event.key === 'ArrowDown') {
              event.preventDefault()
              bump(-1)
            }
          }}
        />
      </div>
      <IconButton
        variant="tertiary"
        size="sm"
        className="nodeScrollDialStep"
        label={`Decrease ${ariaLabel}`}
        disabled={disabled || draft <= min}
        icon={<KeyboardArrowDownIcon fontSize="inherit" />}
        onClick={() => bump(-1)}
      />
      {caption && <span className="nodeScrollDialCaption">{caption}</span>}
    </div>
  )
}

// ===========================================================================
// CurrencyMenu — the Expense Outlay `$` button → popover of 20 currencies.
// ===========================================================================

interface CurrencyMenuProps {
  code: string
  disabled?: boolean
  onSelect: (code: string) => void
}

export const CurrencyMenu = ({ code, disabled = false, onSelect }: CurrencyMenuProps) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const handleClickAway = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [open])

  return (
    <div className="nodeCurrencyMenu" ref={containerRef}>
      <Pressable
        className="nodeCurrencyTrigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Choose currency"
        onClick={() => setOpen((current) => !current)}
      >
        {getCurrencySymbol(code)}
      </Pressable>
      <AnimatePresence>
        {open && (
          <motion.ul
            className="nodeCurrencyList"
            role="listbox"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.14 }}
          >
            {CURRENCIES.map((currency) => (
              <li key={currency.code}>
                <Pressable
                  role="option"
                  aria-selected={currency.code === code}
                  className={currency.code === code ? 'isActive' : ''}
                  onClick={() => {
                    onSelect(currency.code)
                    setOpen(false)
                  }}
                >
                  <span className="nodeCurrencySymbol">{currency.symbol}</span>
                  <span className="nodeCurrencyCode">{currency.code}</span>
                  <span className="nodeCurrencyName">{currency.name}</span>
                </Pressable>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  )
}

// ===========================================================================
// DependencyLinkList — "is blocked by" / "is a blocker for". Growing,
// independent list with a `+` add menu over current-workspace elements.
// ===========================================================================

export interface DependencyOption {
  id: string
  type: 'CHECKLIST'
  title: string
}

interface DependencyLinkListProps {
  title: string
  hint: string
  links: ChecklistNodeLink[]
  options: DependencyOption[]
  disabled?: boolean
  searchMaxLength: number
  onAdd: (option: DependencyOption) => void
  onRemove: (targetId: string) => void
}

const ELEMENT_TYPE_LABEL: Record<'CHECKLIST', string> = {
  CHECKLIST: 'Checklist',
}

export const DependencyLinkList = ({
  title,
  hint,
  links,
  options,
  disabled = false,
  searchMaxLength,
  onAdd,
  onRemove,
}: DependencyLinkListProps) => {
  const [adding, setAdding] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!adding) return
    const handleClickAway = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setAdding(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [adding])

  const linkedIds = new Set(links.map((link) => link.targetId))
  const normalizedQuery = query.trim().toLowerCase()
  const filtered = options
    .filter((option) => !linkedIds.has(option.id))
    .filter((option) =>
      normalizedQuery ? `${option.title} ${ELEMENT_TYPE_LABEL[option.type]}`.toLowerCase().includes(normalizedQuery) : true,
    )
    .slice(0, 40)

  return (
    <div className="nodeDependencyList" ref={containerRef}>
      <div className="nodeDependencyListHeader">
        <span>{title}</span>
        {!disabled && (
          <IconButton
            variant="tertiary"
            size="sm"
            className="nodeDependencyAdd"
            disabled={disabled}
            aria-expanded={adding}
            label={`Add to ${title}`}
            icon={<AddRoundedIcon fontSize="inherit" />}
            onClick={() => setAdding((current) => !current)}
          />
        )}
      </div>

      {links.length === 0 ? (
        <p className="nodeDependencyEmpty">{hint}</p>
      ) : (
        <ul className="nodeDependencyChips">
          {links.map((link) => (
            <li key={link.targetId}>
              <TagChip
                className="nodeDependencyChip"
                removeLabel={`Remove ${link.title}`}
                onRemove={disabled ? undefined : () => onRemove(link.targetId)}
              >
                <span className="nodeDependencyChipType">{ELEMENT_TYPE_LABEL[link.targetType]}</span>
                <span className="nodeDependencyChipTitle">{link.title}</span>
              </TagChip>
            </li>
          ))}
        </ul>
      )}

      <AnimatePresence>
        {adding && (
          <motion.div
            className="nodeDependencyPicker"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.14 }}
          >
            <TextInput
              type="search"
              autoFocus
              className="nodeDependencySearch"
              leadingIcon={<SearchRoundedIcon fontSize="small" />}
              placeholder="Search goals, tasks, subtasks"
              value={query}
              maxLength={searchMaxLength}
              onChange={(next) => setQuery(next.slice(0, searchMaxLength))}
            />
            <ul className="nodeDependencyOptions">
              {filtered.length === 0 ? (
                <li className="nodeDependencyNoOption">No matching items</li>
              ) : (
                filtered.map((option) => (
                  <li key={option.id}>
                    <Pressable
                      onClick={() => {
                        onAdd(option)
                        setQuery('')
                        setAdding(false)
                      }}
                    >
                      <span className="nodeDependencyChipType">{ELEMENT_TYPE_LABEL[option.type]}</span>
                      <span className="nodeDependencyChipTitle">{option.title}</span>
                    </Pressable>
                  </li>
                ))
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ===========================================================================
// StatusSelect — header workflow status dropdown (MUI expand affordance).
// ===========================================================================

interface StatusSelectProps {
  value: ChecklistWorkflowStatus
  disabledReason?: string | null
  disabled?: boolean
  onChange: (value: ChecklistWorkflowStatus) => void
  /** Item context — used to hide ASSESSING/COMPLETED options that must be
   * driven by proof-submission or approver events, not manual selection. */
  item?: {
    proofFileUrl?: string | null
    proofSubmissionId?: string | null
    workflowStatus?: ChecklistWorkflowStatus | null
    proofApproverId?: string | null
  }
  actor?: { uid?: string | null } | null
  optionEval?: (status: ChecklistWorkflowStatus) => { ok: boolean; reason?: string }
}

export const StatusSelect = ({
  value,
  disabledReason = null,
  disabled = false,
  onChange,
  item,
  actor,
  optionEval,
}: StatusSelectProps) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const isDisabled = Boolean(disabledReason || disabled)

  useEffect(() => {
    if (!open) return
    const handleClickAway = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickAway)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickAway)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const options = item ? filterWorkflowStatusOptions(item, actor) : WORKFLOW_STATUS_OPTIONS
  const currentLabel = WORKFLOW_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? 'Created'

  return (
    <div className={`nodeStatusSelect ${isDisabled ? 'isDisabled' : ''}`} ref={containerRef}>
      <Pressable
        className="nodeStatusSelectTrigger"
        disabled={isDisabled}
        aria-disabled={isDisabled ? 'true' : undefined}
        title={disabledReason ?? undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Node status — ${currentLabel}`}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="nodeStatusSelectLabel">{currentLabel}</span>
        <ExpandMoreRoundedIcon className={`nodeStatusSelectIcon ${open ? 'isOpen' : ''}`} fontSize="inherit" />
      </Pressable>
      <AnimatePresence>
        {open && (
          <motion.ul
            className="nodeStatusSelectList"
            role="listbox"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.14 }}
          >
            {options.map((option) => {
              const evalResult = optionEval ? optionEval(option.value) : { ok: true }
              const isGated = !evalResult.ok
              return (
                <li key={option.value}>
                  <Pressable
                    role="option"
                    aria-selected={option.value === value}
                    className={`${option.value === value ? 'isActive' : ''} ${isGated ? 'isGated' : ''}`}
                    title={isGated ? evalResult.reason : undefined}
                    onClick={() => {
                      onChange(option.value)
                      setOpen(false)
                    }}
                  >
                    <span>{option.label}</span>
                    {isGated && (
                      <LockRoundedIcon
                        fontSize="inherit"
                        className="nodeStatusSelectLockIcon"
                        aria-hidden="true"
                      />
                    )}
                  </Pressable>
                </li>
              )
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  )
}
